import { Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import {
  LogoConfig,
  NavigationItem,
  NavigationService,
  ThemeService,
  ToastComponent,
} from '@angular-monorepo/ui';
import { environment } from '../environments/environment';
import { ConfigService } from '../environments/environment.service';
import { MonitoringService } from '@angular-monorepo/monitoring';
import { DashboardService } from '@angular-monorepo/dashboard';
import { HomologationsService } from '@angular-monorepo/homologation';
import { TraceabilityService } from '@angular-monorepo/traceability';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, filter, startWith, Subject, Subscription, takeUntil } from 'rxjs';
import { UsersService } from '@angular-monorepo/users';
import { AuthConfigService, AuthService } from '@angular-monorepo/auth';
import { ProviderService } from '@angular-monorepo/providers';
import { API_URL } from '@angular-monorepo/data-access';
import { MenuItem } from 'primeng/api';
import { AuthBrandPanelComponent } from './components/auth-brand-panel/auth-brand-panel.component';

/** Pantallas sin sesion: llevan el panel de marca. */
const AUTH_ROUTES = ['/login', '/register', '/reset-password'];

@Component({
  imports: [RouterModule, ToastComponent, AuthBrandPanelComponent],
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  standalone: true,
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'Medivelius';
  environment = environment;
  private navigationService = inject(NavigationService);
  private themeService = inject(ThemeService);
  private monitoringService = inject(MonitoringService);
  private dashboardService = inject(DashboardService);
  private homologationsService = inject(HomologationsService);
  private traceabilityService = inject(TraceabilityService);
  private usersService = inject(UsersService);
  private providersService = inject(ProviderService);
  private configService = inject(ConfigService);
  private translate = inject(TranslateService);
  private router = inject(Router);
  private authConfigService = inject(AuthConfigService);
  private authService = inject(AuthService);
  private apiUrl = inject(API_URL);
  private readonly tokenKey = 'auth_token';
  private readonly userKey = 'auth_user';
  private readonly refreshTokenKey = 'refresh_token';
  private langChangeSub!: Subscription;
  private destroy$ = new Subject<void>();

  private userRoles = signal<string[]>([]);
  private providerId = signal<number | string>(0);
  protected isAuthRoute = signal(false);

  protected canAccessProfile = computed(() => {
    const roles = this.userRoles();
    const hasRoles = roles && roles.length > 0;
    const isProvider = roles.includes('provider');
    const hasProviderId = this.providerId() && this.providerId() !== 0;

    return hasRoles && isProvider && hasProviderId;
  });

  constructor() {
    // Por ahora solo tema claro: el oscuro del manual es propuesto y la marca
    // aun no esta mapeada en el esquema oscuro de PrimeNG.
    this.themeService.setTheme('light');

    this.translate.addLangs(['en', 'es']);
    this.translate.setDefaultLang('es');

    const savedLanguage = localStorage.getItem('language') || 'es';
    this.translate.reloadLang(savedLanguage);
    this.translate.use(savedLanguage);

    this.monitoringService.configure({
      apiUrl: this.apiUrl,
    });
    this.usersService.configure({
      apiUrl: this.configService.getApiUrl(),
    });
    // DashboardService rechaza un apiUrl vacio, y aqui es '' (front y API en
    // el mismo origen): se le pasa ese origen explicito.
    this.dashboardService.configure({
      apiUrl: this.configService.getApiUrl() || window.location.origin,
    });
    this.homologationsService.configure({
      apiUrl: this.configService.getApiUrl(),
    });
    this.traceabilityService.configure({
      apiUrl: this.configService.getApiUrl(),
    });
    this.providersService.configure({
      apiUrl: this.configService.getApiUrl(),
    });
    this.setupAuthConfig();
  }

  ngOnInit() {
    // La navegacion inicial es bloqueante y puede terminar antes de este
    // punto: se toma la URL actual y luego se siguen los cambios.
    const updateAuthRoute = (url: string) => this.isAuthRoute.set(AUTH_ROUTES.includes(url.split(/[?#]/)[0]));
    updateAuthRoute(this.router.url);
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe((event) => updateAuthRoute(event.urlAfterRedirects));

    this.setupReactiveNavigation();
    this.updateLogo();
    this.setupFooter();

    this.authService.userRoles$.pipe(takeUntil(this.destroy$)).subscribe((roles) => {
      this.userRoles.set(roles);

      // Actualizar también el provider_id cuando cambien los roles
      this.updateProviderIdFromAuthService();
      this.setupSamayUserNavigation();
    });

    // Cargar valores iniciales
    this.loadInitialAuthData();

    // Configurar traducciones - usar get en lugar de stream para el valor inicial
    this.translate.get(['common.profile', 'common.logout']).subscribe(() => {
      this.setupSamayUserNavigation();
    });

    // Suscribirse a cambios de idioma
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.setupReactiveNavigation();
      this.setupSamayUserNavigation();
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.langChangeSub?.unsubscribe();
  }

  private loadInitialAuthData(): void {
    // Cargar roles iniciales
    const initialRoles = this.authService.getUserRoles();
    this.userRoles.set(initialRoles);

    // Cargar provider_id inicial
    this.updateProviderIdFromAuthService();

    // Configurar navegación inicial
    this.setupSamayUserNavigation();
  }

  private updateProviderIdFromAuthService(): void {
    // Usar el método del AuthService que ya existe
    const providerId = this.authService.getProviderId();
    this.providerId.set(providerId);
  }

  private setupSamayUserNavigation() {
    const profileTitle = this.translate.instant('common.profile');
    const logoutTitle = this.translate.instant('common.logout');

    const samayUserNavigation: MenuItem[] = [];
    if (this.canAccessProfile()) {
      samayUserNavigation.push({
        label: profileTitle,
        icon: 'pi pi-user',
        command: () => this.navigateToProfile(),
      });

      samayUserNavigation.push({
        separator: true,
      });
    }

    samayUserNavigation.push({
      label: logoutTitle,
      icon: 'pi pi-sign-out',
      command: () => this.signOut(),
    });

    this.navigationService.updateUserNavigation(samayUserNavigation);
  }

  private navigateToProfile(): void {
    this.router.navigate(['/perfil']).then((r) => r);
  }

  private signOut(): void {
    // Obtener el valor actual del provider_id ANTES de limpiar los datos
    const currentProviderId = this.providerId();
    const hasProviderId = currentProviderId && currentProviderId !== 0;

    // Limpiar datos
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.userKey);
    localStorage.removeItem(this.refreshTokenKey);

    // Resetear signals después de obtener los valores necesarios
    this.providerId.set(0);
    this.userRoles.set([]);

    // Redirigir según el tipo de usuario
    if (hasProviderId) {
      this.router.navigate(['/login-proveedores']).then((r) => r);
    } else {
      this.router.navigate(['/login']).then((r) => r);
    }
  }

  private setupAuthConfig() {
    this.authConfigService.setConfig({
      apiUrl: this.configService.getApiUrl(),
      profileEndpoint: '/api/users/providers/profile/',
      // Tras el login se entra siempre al Panel de control. Sin esto la lib
      // busca la "primera ruta disponible" con permisos de otros proyectos.
      postLoginRoute: '/dashboard',
    });
  }

  private setupReactiveNavigation(): void {
    combineLatest([
      this.translate.get([
        'sidebar.dashboard',
        'sidebar.traceability',
        'sidebar.connections',
        'sidebar.homologations',
        'sidebar.monitoring',
        'sidebar.providers',
        'sidebar.users',
        'sidebar.roles',
      ]),
      this.authService.permissions$.pipe(startWith([])),
      this.translate.onLangChange.pipe(startWith(null)),
    ]).subscribe(([translations]) => {
      this.updateNavigationWithPermissions(translations);
    });
  }

  private updateNavigationWithPermissions(translations: Record<string, string>): void {
    const modulePermissionMap: { [key: string]: string } = {
      '/dashboard': 'dashboard',
      '/conexiones': 'connections',
      // Mismo back que evolti y mio3: el permiso es el de la lista de ejecuciones.
      '/monitoreo': 'workexecutionlist',
      '/roles-y-permisos': 'roles',
      '/proveedores': 'providers',
      '/usuarios': 'users',
      '/workflows': 'workflows',
      '/homologaciones': 'homologations',
    };

    // Mismos modulos que EVOLTI.
    const allNavigationItems: NavigationItem[] = [
      {
        name: translations['sidebar.dashboard'],
        href: '/dashboard',
        icon: 'pi pi-home',
        current: false,
      },
      {
        name: translations['sidebar.traceability'],
        href: '/trazabilidad',
        icon: 'pi pi-search',
        current: false,
      },
      {
        name: translations['sidebar.connections'],
        href: '/conexiones',
        icon: 'pi pi-globe',
        current: false,
      },
      {
        name: translations['sidebar.homologations'],
        href: '/homologaciones',
        icon: 'pi pi-sitemap',
        current: false,
      },
      {
        name: translations['sidebar.monitoring'],
        href: '/monitoreo',
        icon: 'pi pi-chart-line',
        current: false,
      },
      // Roles y permisos (sin pantalla) y Proveedores no se muestran, aunque
      // el back mande sus permisos.
      {
        name: translations['sidebar.users'],
        href: '/usuarios',
        icon: 'pi pi-users',
        current: false,
      },
    ];

    const filteredNavigationItems = allNavigationItems.filter((item) => {
      const permissionName = modulePermissionMap[item.href];

      // Panel de control y su historial de registro no dependen de un permiso.
      if (item.href === '/dashboard' || item.href === '/trazabilidad') {
        return true;
      }

      // Sin confirmar que nombre manda el back de medivelius para monitoreo:
      // se acepta el de evolti/mio3 y el de la plantilla.
      if (item.href === '/monitoreo') {
        return (
          this.authService.hasModuleAccess('workexecutionlist') ||
          this.authService.hasModuleAccess('monitoring')
        );
      }

      if (!permissionName) {
        return false;
      }

      return this.authService.hasModuleAccess(permissionName);
    });

    // El layout compartido solo marca el activo al navegar; en la carga
    // inicial y al rehacer el menu (idioma, permisos) se marca aqui.
    const currentPath = this.router.url.split(/[?#]/)[0];
    this.navigationService.updateNavigation(
      filteredNavigationItems.map((item) => ({
        ...item,
        current: currentPath === item.href || currentPath.startsWith(`${item.href}/`),
      })),
    );
  }

  /**
   * Logo de Medivelius a color: va sobre blanco en el login y en el menu
   * lateral (manual, seccion 02). Colapsado, el CSS lo cambia por el isotipo.
   */
  private updateLogo() {
    const logo: LogoConfig = {
      type: 'image',
      src: 'assets/brand/medivelius-logo.svg',
      alt: 'Medivelius',
      class: 'medivelius-logo',
    };

    this.navigationService.updateLogo(logo);
  }

  private setupFooter() {
    this.navigationService.updateFooterConfig({
      showDefault: false,
      hideForgotPasswordLink: false,
      // Las cuentas las crea un administrador: sin registro abierto.
      hideRegisterLink: true,
      customElements: [
        {
          type: 'component',
          content: 'lib-i18n',
          props: {
            class: 'block',
            variant: 'toggle',
          },
        },
      ],
    });
  }
}
